export const getElementFont = (el: HTMLElement): string => {
  const defaultFontWeight = 'normal';
  const defaultFontSize = '16px';
  const defaultFontFamily = 'Open Sans';
  if (!window) {
    return `${defaultFontWeight} ${defaultFontSize} ${defaultFontFamily}`;
  }
  const style = window.getComputedStyle(el, null);
  const fontWeight = style.getPropertyValue('font-weight') || defaultFontWeight;
  const fontSize = style.getPropertyValue('font-size') || '16px';
  const fontFamily = style.getPropertyValue('font-family') || 'Times New Roman';
  return `${fontWeight} ${fontSize} ${fontFamily}`;
};

export const getTextWidth = (
  text: string,
  canvasEl: HTMLCanvasElement,
  font: string
): number => {
  const context = canvasEl.getContext('2d');
  if (!context) {
    throw new Error("Can't get canvas 2d context");
  }
  context.font = font;
  return context.measureText(text)?.width;
};

/**
 * Resolves a CSS length/expression (incl. calc, vh, %, em, etc.)
 * to a pixel value string like "742px".
 *
 * @param expr A CSS length or expression, e.g. "calc(100vh - 300px)"
 * @param context If expr uses %, it will resolve against this element's size.
 *                     If omitted, % heights may resolve to 0 depending on the DOM.
 * @returns Pixel value string (e.g. "742px") or null if it can't be resolved.
 */
export const resolveCssLengthToPx = (
  expr: string | number,
  context: HTMLElement = document.documentElement
): number => {
  if (!expr || expr === 'auto' || expr === 'none') {
    return 0;
  }

  // Normalize calc spacing a bit to avoid older parsing quirks
  const normalized = String(expr).replace(
    /calc\((.*)\)/i,
    (_, inner) => `calc(${inner.replace(/([+\-*/])/g, ' $1 ')})`
  );

  // A container ensures % heights can resolve (must have explicit size)
  const container = document.createElement('div');
  const probe = document.createElement('div');

  Object.assign(container.style, {
    position: 'absolute',
    left: '-99999px',
    top: '0',
    width: '0',
    height: '0',
    overflow: 'hidden',
    // Contain to avoid layout side-effects in some browsers
    contain: 'layout style size',
  } as CSSStyleDeclaration);

  // Give the container a known block-size when resolving percentages.
  // If expr never uses %, this is harmless.
  container.style.height =
    (window.innerHeight || context.clientHeight || 0) + 'px';

  Object.assign(probe.style, {
    // Make it resolve height independently of content
    boxSizing: 'content-box',
    padding: '0',
    margin: '0',
    border: '0',
    position: 'absolute',
    height: normalized,
    // Use contain to keep it isolated and fast
    contain: 'layout style size',
    // Avoid transforms that might create different CBs
    transform: 'none',
  } as CSSStyleDeclaration);

  container.appendChild(probe);

  context.appendChild(container);

  const h = getComputedStyle(probe).height;
  container.remove();

  return parseInt(h);
};
